import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Hero from '../components/sections/Hero';
import WowSection from '../components/sections/WowSection';

const VideoTestimonials = lazy(() => import('../components/sections/VideoTestimonials'));
const StepProcess = lazy(() => import('../components/sections/StepProcess'));
const InlineCTA = lazy(() => import('../components/sections/InlineCTA'));
const Stats = lazy(() => import('../components/sections/Stats'));
const Testimonials = lazy(() => import('../components/sections/Testimonials'));
const FAQ = lazy(() => import('../components/sections/FAQ'));
const ForWho = lazy(() => import('../components/sections/ForWho'));
const Confiance = lazy(() => import('../components/sections/Confiance'));
const CTASection = lazy(() => import('../components/sections/CTASection'));

const Lazy = ({ children }) => (
  <Suspense fallback={<div style={{ minHeight: 200 }} />}>
    {children}
  </Suspense>
);

const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Consultauto",
  "description": "Firme de conseil automobile indépendante à Montréal. Accompagnement complet pour l'achat et la location de véhicule neuf.",
  "url": "https://consultauto.ca",
  "telephone": "+1-438-817-7277",
  "email": "info@consultauto.ca",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Montréal",
    "addressRegion": "QC",
    "addressCountry": "CA"
  },
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": { "@type": "GeoCoordinates", "latitude": 45.5017, "longitude": -73.5673 },
    "geoRadius": "80000"
  },
  "sameAs": [
    "https://www.facebook.com/consultauto.ca",
    "https://www.instagram.com/consultauto.ca",
    "https://www.tiktok.com/@consultauto",
    "https://www.linkedin.com/company/consultauto"
  ]
};

const HomePage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  return (
    <div className="min-h-screen font-sans" data-testid="home-page">
      <Helmet>
        <title>{fr ? 'Consultauto — Conseil automobile indépendant | Véhicule neuf Montréal' : 'Consultauto — Independent Automotive Advisor | New Vehicle Montreal'}</title>
        <meta name="description" content={fr
          ? "Consultauto — Firme de conseil automobile indépendante à Montréal. Accompagnement complet pour l'achat et la location de véhicule neuf. Analyse, sélection, essais à domicile, négociation automobile, livraison."
          : "Consultauto — Independent automotive advisory firm in Montreal. Complete support for new vehicle purchase and lease. Analysis, selection, home test drives, negotiation, delivery."
        } />
        <meta name="keywords" content="achat véhicule neuf Montréal, véhicule neuf, location véhicule neuf, consultant automobile Québec, négociation automobile Montréal" />
        <meta property="og:title" content={fr ? 'Consultauto — Conseil automobile indépendant | Véhicule neuf Montréal' : 'Consultauto — Independent Automotive Advisor | New Vehicle Montreal'} />
        <meta property="og:description" content={fr
          ? "Accompagnement complet pour l'achat et la location de véhicule neuf à Montréal. 13+ ans d'expérience, 25M$ négociés annuellement."
          : "Complete support for new vehicle purchase and lease in Montreal. 13+ years of experience, $25M negotiated annually."
        } />
        <link rel="canonical" href="https://consultauto.ca/" />
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <WowSection />
        <Lazy><VideoTestimonials /></Lazy>
        <Lazy><StepProcess /></Lazy>
        <Lazy><InlineCTA /></Lazy>
        <Lazy><Stats /></Lazy>
        <Lazy><Testimonials /></Lazy>
        <Lazy><InlineCTA /></Lazy>
        <Lazy><FAQ /></Lazy>
        <Lazy><ForWho /></Lazy>
        <Lazy><Confiance /></Lazy>
        <Lazy><CTASection /></Lazy>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
